import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import api from "../lib/api";
import { fmtPrice } from "../lib/format";

const RANGES = ["1M", "6M", "1Y"];

const ChartTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-border rounded-sm px-3 py-2 text-xs font-mono">
      <div className="text-muted-foreground">{label}</div>
      <div className="text-white font-medium">{fmtPrice(payload[0].value, currency)}</div>
    </div>
  );
};

export const PriceChart = ({ ticker }) => {
  const [range, setRange] = useState("1Y");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/stocks/${ticker}/history`, { params: { range } }).then((r) => {
      if (!cancelled) setData(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [ticker, range]);

  const points = data?.points || [];
  const up = points.length > 1 && points[points.length - 1].close >= points[0].close;
  const color = up ? "#10b981" : "#ef4444";

  return (
    <div className="widget p-4 h-full flex flex-col" data-testid="price-chart-widget">
      <div className="flex items-center justify-between mb-3">
        <span className="overline-label">Price · {range}</span>
        <div className="flex border border-border rounded-sm overflow-hidden">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`chart-range-${r}`}
              onClick={() => setRange(r)}
              className={`px-3 py-1 text-xs font-mono transition-colors duration-150 ${
                range === r ? "bg-primary text-white" : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-[280px]">
        {points.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={points} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
              <CartesianGrid stroke="#27272a" strokeOpacity={0.4} vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                minTickGap={48}
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                width={56}
                tickFormatter={(v) => v.toLocaleString()}
              />
              <Tooltip content={<ChartTooltip currency={data?.currency} />} />
              <Area
                type="monotone"
                dataKey="close"
                stroke={color}
                strokeWidth={2}
                fill={color}
                fillOpacity={0.07}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground font-mono">
            Loading chart…
          </div>
        )}
      </div>
    </div>
  );
};
